import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import api from '../utils/axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { StatCard } from './kpis/components/StatCard';
import { KpiTimeChart } from './kpis/components/KpiTimeChart';
import { fmtNum, fmtNumShort, calcDelta, CHART_COLORS } from './kpis/components/kpiUtils';
import {
  UserPlus, RefreshCw, Users, Clock, ShieldCheck, Settings, CalendarDays, CheckCircle2, Search, Camera,
  FileSpreadsheet, FileText,
} from 'lucide-react';

const INTERVALOS = [5, 10, 15, 30, 60];

const AltasDashboard = () => {
  const [fecha, setFecha] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [showCfg, setShowCfg] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [showCompletaron, setShowCompletaron] = useState(false);
  const [q, setQ] = useState('');
  const [fVal, setFVal] = useState('');
  const [fEstado, setFEstado] = useState('');
  const pollRef = useRef(null);

  const cargar = useCallback(async (f) => {
    try {
      const r = await api.get('/clientes/altas', { params: f ? { fecha: f } : {} });
      setData(r.data);
      if (!fecha && r.data?.fecha) setFecha(r.data.fecha);
    } catch (e) {
      toast.error('Error al cargar el dashboard de altas');
    } finally {
      setLoading(false);
    }
  }, [fecha]);

  const cargarConfig = useCallback(async () => {
    try {
      const r = await api.get('/clientes/altas/config');
      setCfg(r.data);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { cargar(); cargarConfig(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresco liviano de la vista cada 60s (lee del snapshot en Mongo)
  useEffect(() => {
    const t = setInterval(() => { cargar(fecha); }, 60000);
    return () => clearInterval(t);
  }, [fecha, cargar]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const onFecha = (e) => { const f = e.target.value; setFecha(f); cargar(f); };

  const actualizarAhora = async () => {
    setRefreshing(true);
    try {
      const r = await api.post('/clientes/altas/refrescar');
      if (r.data?.success === false) { toast.info(r.data.message || 'Ya hay una actualización en curso'); }
      else { toast.success('Actualizando billeteras desde Waya…'); }
      // Poll del estado hasta que termine
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const s = await api.get('/clientes/altas/status');
          if (s.data?.status !== 'en_ejecucion' || s.data?.is_stale) {
            clearInterval(pollRef.current); pollRef.current = null;
            setRefreshing(false);
            await cargar(fecha); await cargarConfig();
            if (s.data?.status === 'ok') toast.success(`Snapshot actualizado (${fmtNum(s.data?.total)} billeteras)`);
            else if (s.data?.status === 'error' || s.data?.is_stale) toast.error(s.data?.error || 'Falló la actualización. Reintentá.');
          }
        } catch (e) { /* seguir intentando */ }
      }, 5000);
    } catch (e) {
      toast.error('No se pudo iniciar la actualización');
      setRefreshing(false);
    }
  };

  const exportar = async (formato, tipo = 'altas') => {
    setExporting(`${tipo}-${formato}`);
    try {
      const params = { fecha: data?.fecha, formato, tipo };
      if (tipo === 'altas') {
        if (q.trim()) params.q = q.trim();
        if (fVal) params.validacion = fVal;
        if (fEstado) params.estado = fEstado;
      }
      const r = await api.get('/clientes/altas/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tipo === 'completaron' ? 'completaron' : 'altas'}_${data?.fecha}.${formato === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('No se pudo exportar');
    } finally {
      setExporting(null);
    }
  };

  const guardarConfig = async (enabled, interval_minutes) => {
    setSavingCfg(true);
    try {
      const r = await api.put('/clientes/altas/config', { enabled, interval_minutes });
      setCfg((c) => ({ ...c, ...r.data }));
      toast.success('Configuración guardada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar la configuración');
      cargarConfig();
    } finally { setSavingCfg(false); }
  };

  const comp = data?.comparativas || {};
  const totalDia = data?.total_dia || 0;
  const deltaAyer = calcDelta(totalDia, comp.ayer);
  const deltaSemPas = calcDelta(totalDia, comp.semana_pasada_mismo_dia);

  const horaData = useMemo(
    () => (data?.por_hora || []).map((h) => ({ label: h.label, count: h.count })),
    [data]
  );
  const valData = useMemo(
    () => (data?.por_validacion || []).map((v) => ({ validacion: v.validacion, count: v.count })),
    [data]
  );
  const tabla = data?.tabla || [];
  const completaron = data?.completaron || [];
  const completaronTotal = data?.completaron_total ?? completaron.length;
  const meta = { ...(cfg?.meta || {}), ...(data?.meta || {}) };
  const esAdmin = data?.es_admin || cfg?.es_admin;
  const hist = data?.historial || {};

  const valOpts = useMemo(() => Array.from(new Set(tabla.map((r) => r.validacion).filter(Boolean))), [tabla]);
  const estadoOpts = useMemo(() => Array.from(new Set(tabla.map((r) => r.estado).filter(Boolean))), [tabla]);
  const tablaFiltrada = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return tabla.filter((r) => {
      if (fVal && r.validacion !== fVal) return false;
      if (fEstado && r.estado !== fEstado) return false;
      if (qq) {
        const hay = `${r.nombre || ''} ${r.cuit || ''} ${r.cvu || ''} ${r.dni || ''} ${r.email || ''}`.toLowerCase();
        if (!hay.includes(qq)) return false;
      }
      return true;
    });
  }, [tabla, q, fVal, fEstado]);

  const esHoy = data?.fecha && data?.hoy && data.fecha === data.hoy;
  const fuente = data?.fuente;
  const enEjecucion = (meta?.status === 'en_ejecucion' && !meta?.is_stale) || refreshing;

  const fmtDT = (iso) => { try { return iso ? new Date(iso).toLocaleString('es-AR') : '—'; } catch { return '—'; } };

  if (loading) return <div className="p-8 text-slate-500" data-testid="altas-loading">Cargando altas…</div>;

  return (
    <div className="p-6 lg:p-8 pt-20 sm:pt-6 lg:pt-8 max-w-7xl mx-auto" data-testid="altas-dashboard">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 rounded-xl bg-emerald-500/10"><UserPlus className="h-6 w-6 text-emerald-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Altas en vivo</h1>
            <p className="text-sm text-slate-500">Nuevas billeteras Waya creadas (según la fecha de {'"Movimiento"'})</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-1.5">
            <CalendarDays className="h-4 w-4 text-slate-400" />
            <input
              type="date" value={fecha} onChange={onFecha} max={data?.hoy}
              className="bg-transparent text-sm text-slate-700 dark:text-slate-200 outline-none"
              data-testid="altas-fecha-input"
            />
          </div>
          <Button
            onClick={actualizarAhora} disabled={enEjecucion}
            className="bg-emerald-600 hover:bg-emerald-700 text-white active:scale-95 transition-transform"
            data-testid="altas-refrescar-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${enEjecucion ? 'animate-spin' : ''}`} />
            {enEjecucion ? 'Actualizando…' : 'Actualizar ahora'}
          </Button>
          {esAdmin && (
            <Button variant="outline" onClick={() => setShowCfg((s) => !s)} data-testid="altas-config-toggle">
              <Settings className="h-4 w-4 mr-1" /> Configuración
            </Button>
          )}
        </div>
      </div>

      {/* Panel de configuración (admin) */}
      {esAdmin && showCfg && cfg && (
        <Card className="p-5 mb-6 border-slate-200 dark:border-slate-800" data-testid="altas-config-panel">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Switch
                checked={!!cfg.enabled}
                onCheckedChange={(v) => guardarConfig(v, cfg.interval_minutes)}
                disabled={savingCfg}
                data-testid="altas-config-switch"
              />
              <div>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Actualización automática</p>
                <p className="text-xs text-slate-500">{cfg.enabled ? `Activa · cada ${cfg.interval_minutes} min` : 'Apagada'}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-500 mr-1">Intervalo:</span>
              {INTERVALOS.map((m) => (
                <button
                  key={m}
                  onClick={() => guardarConfig(cfg.enabled, m)}
                  disabled={savingCfg || !cfg.enabled}
                  className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                    cfg.interval_minutes === m
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200'
                  } ${(!cfg.enabled) ? 'opacity-40 cursor-not-allowed' : ''}`}
                  data-testid={`altas-config-intervalo-${m}`}
                >
                  {m} min
                </button>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Número grande + comparativas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="p-6 lg:col-span-1 border-slate-200 dark:border-slate-800 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-500/10 dark:to-slate-900" data-testid="altas-numero-grande">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
              {esHoy ? 'Altas de hoy' : 'Altas del día'}
            </p>
            <UserPlus className="h-5 w-5 text-emerald-600" />
          </div>
          {!esHoy && fuente === 'foto_diaria' && (
            <span className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700" data-testid="altas-fuente-foto">
              <Camera className="h-3 w-3" /> Foto guardada
            </span>
          )}
          {!esHoy && fuente === 'estimado' && (
            <span className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid="altas-fuente-estimado">
              Estimado (sin foto guardada)
            </span>
          )}
          <p className="text-5xl sm:text-6xl font-bold tracking-tight text-slate-900 dark:text-white mt-3 tabular-nums" data-testid="altas-total-dia">
            {fmtNum(totalDia)}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {deltaAyer && (
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${deltaAyer.pct >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                {deltaAyer.pct >= 0 ? '▲' : '▼'} {Math.abs(deltaAyer.pct).toFixed(0)}% vs ayer
              </span>
            )}
            {deltaSemPas && (
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${deltaSemPas.pct >= 0 ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'}`}>
                {deltaSemPas.pct >= 0 ? '▲' : '▼'} {Math.abs(deltaSemPas.pct).toFixed(0)}% vs sem. pasada
              </span>
            )}
          </div>
          {hist.aplicado && (
            <p className="text-xs text-slate-500 mt-2" data-testid="altas-historial-nota">
              <span className="font-semibold text-slate-700 dark:text-slate-300">{fmtNum(hist.excluidos)}</span> cuenta(s) descontada(s) por figurar en el Historial de ayer ({hist.fecha})
            </p>
          )}
        </Card>

        <div className="lg:col-span-2 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Ayer" value={fmtNum(comp.ayer)} icon={CalendarDays} accent="#6366f1" testId="altas-comp-ayer" />
          <StatCard label="Mismo día sem. pasada" value={fmtNum(comp.semana_pasada_mismo_dia)} icon={Clock} accent="#0ea5e9" testId="altas-comp-semana-pasada" />
          <StatCard label="Esta semana" value={fmtNum(comp.semana_actual)} icon={Users} accent="#10b981" testId="altas-comp-semana" />
          <StatCard label="Este mes" value={fmtNum(comp.mes_actual)} icon={Users} accent="#8b5cf6" testId="altas-comp-mes" />
        </div>
      </div>

      {/* Completaron el alta en otro día */}
      {(esHoy || completaronTotal > 0) && (
        <Card className="p-5 lg:p-6 mb-6 border-indigo-200 dark:border-indigo-900 bg-indigo-50/40 dark:bg-indigo-500/5" data-testid="altas-completaron">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-indigo-500/10"><CheckCircle2 className="h-6 w-6 text-indigo-600" /></div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-indigo-700 dark:text-indigo-400">Completaron el alta en otro día</p>
                <p className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white tabular-nums" data-testid="altas-completaron-total">{fmtNum(completaronTotal)}</p>
                <p className="text-xs text-slate-500">Ya existían (incompletas) y hoy figuran con CVU y validación Aprobada</p>
              </div>
            </div>
            {completaronTotal > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setShowCompletaron((s) => !s)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-indigo-300 text-indigo-700 hover:bg-indigo-100 transition-colors"
                  data-testid="altas-completaron-toggle"
                >
                  {showCompletaron ? 'Ocultar detalle' : 'Ver detalle'}
                </button>
                <button
                  onClick={() => exportar('excel', 'completaron')} disabled={exporting}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 transition-colors"
                  data-testid="altas-completaron-export-excel"
                >
                  <FileSpreadsheet className="h-4 w-4" /> {exporting === 'completaron-excel' ? 'Generando…' : 'Excel'}
                </button>
                <button
                  onClick={() => exportar('pdf', 'completaron')} disabled={exporting}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40 transition-colors"
                  data-testid="altas-completaron-export-pdf"
                >
                  <FileText className="h-4 w-4" /> {exporting === 'completaron-pdf' ? 'Generando…' : 'PDF'}
                </button>
              </div>
            )}
          </div>
          {showCompletaron && completaronTotal > 0 && (
            <div className="overflow-x-auto mt-4 border-t border-indigo-100 dark:border-indigo-900 pt-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-500">
                    <th className="text-left font-medium px-3 py-2">Hora</th>
                    <th className="text-left font-medium px-3 py-2">Nombre / Razón social</th>
                    <th className="text-left font-medium px-3 py-2">Email</th>
                    <th className="text-left font-medium px-3 py-2">CUIT</th>
                    <th className="text-left font-medium px-3 py-2">Estado hoy</th>
                    <th className="text-left font-medium px-3 py-2">Validación: ayer → hoy</th>
                  </tr>
                </thead>
                <tbody>
                  {completaron.map((r, i) => {
                    const hora = r.movimiento_raw ? (r.movimiento_raw.split(' ')[1] || '—') : '—';
                    return (
                      <tr key={i} className="border-t border-indigo-100/70 dark:border-indigo-900/60" data-testid={`altas-completaron-fila-${i}`}>
                        <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-slate-500">{hora}</td>
                        <td className="px-3 py-2 font-medium text-slate-700 dark:text-slate-200">{r.nombre || '—'}</td>
                        <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">{r.email || '—'}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.cuit || '—'}</td>
                        <td className="px-3 py-2">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{r.estado || '—'}</span>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <span className="text-amber-700">{r.validacion_ayer || 'Sin dato'}{r.tenia_cvu_ayer ? '' : ' · sin CVU'}</span>
                          <span className="text-slate-400 mx-1">→</span>
                          <span className="text-emerald-700 font-semibold">{r.validacion || '—'}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Gráfico por hora */}
      <Card className="p-5 lg:p-6 mb-6" data-testid="altas-por-hora">
        <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Altas por hora del día</h2>
        <KpiTimeChart
          data={horaData} xKey="label"
          series={[{ key: 'count', name: 'Altas', color: '#059669' }]}
          type="barras" height={300} valueFormatter={fmtNum} axisFormatter={fmtNumShort}
          showLegend={false} testId="altas-hora-chart"
        />
      </Card>

      {/* Desglose por validación de identidad */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-5 lg:p-6" data-testid="altas-por-validacion">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="h-4 w-4 text-slate-400" />
            <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">Por validación de identidad</h2>
          </div>
          {valData.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400">Sin altas para esta fecha.</p>
          ) : (
            <>
              <KpiTimeChart
                data={valData} xKey="validacion"
                series={[{ key: 'count', name: 'Billeteras', color: '#4f46e5' }]}
                type="barras" barLayout="vertical" cellColors={CHART_COLORS}
                height={Math.max(140, valData.length * 46)} valueFormatter={fmtNum} axisFormatter={fmtNumShort}
                showLegend={false} testId="altas-validacion-chart"
              />
              <div className="mt-3 flex flex-wrap gap-2">
                {valData.map((v, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300" data-testid={`altas-val-chip-${i}`}>
                    <span className="font-semibold text-slate-800 dark:text-slate-100">{fmtNum(v.count)}</span> · {v.validacion}
                  </span>
                ))}
              </div>
            </>
          )}
        </Card>

        <Card className="p-5 lg:p-6" data-testid="altas-por-estado">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="h-4 w-4 text-slate-400" />
            <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">Por estado de la cuenta</h2>
          </div>
          {(data?.por_estado || []).length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400">Sin altas para esta fecha.</p>
          ) : (
            <div className="space-y-2">
              {(data?.por_estado || []).map((e, i) => {
                const pct = totalDia ? (e.count / totalDia) * 100 : 0;
                const hab = String(e.estado || '').toLowerCase().startsWith('habilitad');
                return (
                  <div key={i} data-testid={`altas-estado-row-${i}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-slate-600 dark:text-slate-300">{e.estado}</span>
                      <span className="font-semibold text-slate-800 dark:text-slate-100 tabular-nums">{fmtNum(e.count)} <span className="text-slate-400 font-normal">({pct.toFixed(0)}%)</span></span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                      <div className={`h-full rounded-full ${hab ? 'bg-emerald-500' : 'bg-rose-400'}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Tabla de altas */}
      <Card className="p-0 overflow-hidden" data-testid="altas-tabla">
        <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 shrink-0">
            Altas del día ({fmtNum(tablaFiltrada.length)}{tablaFiltrada.length !== tabla.length ? ` de ${fmtNum(tabla.length)}` : ''})
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => exportar('excel')} disabled={exporting || tabla.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="altas-export-excel"
            >
              <FileSpreadsheet className="h-4 w-4" /> {exporting === 'altas-excel' ? 'Generando…' : 'Excel'}
            </button>
            <button
              onClick={() => exportar('pdf')} disabled={exporting || tabla.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="altas-export-pdf"
            >
              <FileText className="h-4 w-4" /> {exporting === 'altas-pdf' ? 'Generando…' : 'PDF'}
            </button>
            <div className="relative">
              <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar nombre, CUIT, CVU, DNI…"
                className="pl-8 pr-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 outline-none focus:ring-2 focus:ring-emerald-500/40 w-64"
                data-testid="altas-tabla-buscar"
              />
            </div>
            <select
              value={fVal} onChange={(e) => setFVal(e.target.value)}
              className="py-1.5 px-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 outline-none"
              data-testid="altas-tabla-filtro-validacion"
            >
              <option value="">Validación: todas</option>
              {valOpts.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <select
              value={fEstado} onChange={(e) => setFEstado(e.target.value)}
              className="py-1.5 px-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 outline-none"
              data-testid="altas-tabla-filtro-estado"
            >
              <option value="">Estado: todos</option>
              {estadoOpts.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
            {(q || fVal || fEstado) && (
              <button
                onClick={() => { setQ(''); setFVal(''); setFEstado(''); }}
                className="text-xs text-slate-500 hover:text-slate-700 underline"
                data-testid="altas-tabla-limpiar"
              >
                Limpiar
              </button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800 text-slate-500">
                <th className="text-left font-medium px-4 py-2.5">Hora</th>
                <th className="text-left font-medium px-4 py-2.5">Nombre / Razón social</th>
                <th className="text-left font-medium px-4 py-2.5">Email</th>
                <th className="text-left font-medium px-4 py-2.5">CUIT</th>
                <th className="text-left font-medium px-4 py-2.5">DNI</th>
                <th className="text-left font-medium px-4 py-2.5">CVU</th>
                <th className="text-left font-medium px-4 py-2.5">Estado</th>
                <th className="text-left font-medium px-4 py-2.5">Validación identidad</th>
              </tr>
            </thead>
            <tbody>
              {tablaFiltrada.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400" data-testid="altas-tabla-vacia">{tabla.length === 0 ? 'No hay altas registradas para esta fecha.' : 'Ninguna alta coincide con los filtros.'}</td></tr>
              ) : tablaFiltrada.map((r, i) => {
                const hab = String(r.estado || '').toLowerCase().startsWith('habilitad');
                const hora = r.movimiento_raw ? (r.movimiento_raw.split(' ')[1] || '—') : '—';
                return (
                  <tr key={i} className="border-t border-slate-100 dark:border-slate-800" data-testid={`altas-fila-${i}`}>
                    <td className="px-4 py-2.5 whitespace-nowrap font-mono text-xs text-slate-500">{hora}</td>
                    <td className="px-4 py-2.5 font-medium text-slate-700 dark:text-slate-200">{r.nombre || '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600 dark:text-slate-300">{r.email || '—'}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{r.cuit || '—'}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{r.dni || '—'}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{r.cvu || '—'}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${hab ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{r.estado || '—'}</span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{r.validacion || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-xs text-slate-400 mt-4" data-testid="altas-meta">
        {fuente === 'foto_diaria'
          ? `📸 Foto guardada del ${data?.fecha} (congelada: ${fmtDT(data?.foto_actualizada)})`
          : `Fuente: Waya · Reporte de Billeteras. Última actualización: ${fmtDT(meta?.last_run)}`}
        {fuente !== 'foto_diaria' && meta?.total ? ` · ${fmtNum(meta.total)} billeteras en el snapshot` : ''}
        {fuente !== 'foto_diaria' && (cfg?.enabled ? ` · auto cada ${cfg.interval_minutes} min` : ' · auto apagado')}
        {meta?.status === 'error' && meta?.error ? ` · ⚠ ${meta.error}` : ''}
      </p>
    </div>
  );
};

export default AltasDashboard;
